import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Lock, CalendarDays } from "lucide-react";
import { IMG } from "@/lib/images";
import { COMPANY, SERVICE_AREAS, SERVICE_TYPES } from "@/lib/data";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { BTN_PRIMARY } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuote } from "@/context/QuoteContext";

const info = [
  { icon: Phone, title: "PHONE", value: COMPANY.phone },
  { icon: Mail, title: "EMAIL", value: COMPANY.email },
  { icon: MapPin, title: "SERVICE AREA", value: "Serving communities across Texas" },
];

const pins = [
  { top: "30%", left: "60%" }, { top: "40%", left: "50%" }, { top: "38%", left: "70%" },
  { top: "50%", left: "58%" }, { top: "56%", left: "46%" }, { top: "62%", left: "64%" },
  { top: "70%", left: "54%" }, { top: "48%", left: "38%" },
];

export default function Contact() {
  const [service, setService] = useState("");
  const { openQuote } = useQuote();

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Thanks! This is a demo form — your request would be sent to our team.");
  };

  return (
    <div data-testid="contact-page">
      <PageHero title="CONTACT US" text="Have a question or ready to schedule your solar panel cleaning? We're here to help.">
        <div className="mt-6 space-y-3">
          {["Fast response", "Free, no-obligation quotes"].map((t) => (
            <div key={t} className="flex items-center gap-3 text-[15px] font-medium text-[#0A2342]">
              <CheckCircle2 size={20} className="text-[#1E5EDB]" /> {t}
            </div>
          ))}
        </div>
      </PageHero>

      {/* GET IN TOUCH + FORM */}
      <section className="mx-auto max-w-[1200px] px-5 lg:px-8 py-16">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold text-[#0A2342]">GET IN TOUCH</h2>
            <span className="mt-4 block h-1 w-14 rounded-full bg-[#1E5EDB]" />
            <div className="mt-8 space-y-7">
              {info.map((it) => (
                <div key={it.title} className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#1E5EDB]">
                    <it.icon size={22} className="text-white" />
                  </span>
                  <div>
                    <p className="font-display text-[13px] font-bold tracking-wide text-[#1E5EDB]">{it.title}</p>
                    <p className="mt-0.5 break-all text-[15px] text-[#0A2342]">{it.value}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#1E5EDB]">
                  <Clock size={22} className="text-white" />
                </span>
                <div>
                  <p className="font-display text-[13px] font-bold tracking-wide text-[#1E5EDB]">BUSINESS HOURS</p>
                  <p className="mt-0.5 text-[15px] text-[#0A2342]">Monday – Friday&nbsp;&nbsp;7:00 AM – 6:00 PM</p>
                  <p className="text-[15px] text-[#0A2342]">Saturday&nbsp;&nbsp;8:00 AM – 2:00 PM</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="rounded-2xl bg-[#F4F7FB] p-6 md:p-8">
              <h3 className="font-display text-2xl font-extrabold text-[#0A2342]">SEND US A MESSAGE</h3>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <Input data-testid="input-first-name" placeholder="First Name" className="h-12 bg-white" required />
                <Input data-testid="input-last-name" placeholder="Last Name" className="h-12 bg-white" required />
                <Input data-testid="input-email" type="email" placeholder="Email Address" className="h-12 bg-white sm:col-span-1" required />
                <Input data-testid="input-phone" placeholder="Phone Number" className="h-12 bg-white sm:col-span-1" />
              </div>
              <div className="mt-4">
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger data-testid="select-service" className="h-12 bg-white text-[#5B6B7F] data-[placeholder]:text-[#5B6B7F]">
                    <SelectValue placeholder="Service Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Textarea data-testid="input-message" placeholder="Message or project details" rows={5} className="mt-4 bg-white" />
              <button type="submit" data-testid="contact-submit" className={`${BTN_PRIMARY} mt-5 w-full py-4 text-[15px]`}>SEND MESSAGE</button>
              <p className="mt-4 flex items-center justify-center gap-2 text-[13px] text-[#5B6B7F]">
                <Lock size={14} /> Your information is never shared.
              </p>
            </form>
          </Reveal>
        </div>
      </section>

      {/* AREAS WE SERVE */}
      <section className="border-t border-[#E4EBF3]">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8 py-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <div className="relative mx-auto w-full max-w-[420px]">
                <img src={IMG.texas} alt="Texas service area map" className="w-full opacity-90" />
                {pins.map((p, i) => (
                  <MapPin key={i} size={24} className="absolute text-[#1E5EDB]" style={{ top: p.top, left: p.left }} fill="#1E5EDB" strokeWidth={1} />
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-3xl font-extrabold text-[#0A2342]">AREAS WE SERVE</h2>
              <span className="mt-4 block h-1 w-14 rounded-full bg-[#1E5EDB]" />
              <p className="mt-5 text-[16px] leading-relaxed text-[#5B6B7F]">
                Serving residential, commercial, and utility-scale solar projects across Texas, including these communities and surrounding areas.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                {SERVICE_AREAS.map((city) => (
                  <div key={city} className="flex items-center gap-2 text-[14px] text-[#0A2342]">
                    <CheckCircle2 size={17} className="shrink-0 text-[#1E5EDB]" /> {city}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-[1200px] px-5 lg:px-8 pb-20">
        <div className="flex flex-col items-start gap-6 rounded-2xl bg-[#F4F7FB] p-8 md:flex-row md:items-center md:justify-between md:p-10">
          <div className="flex items-start gap-5">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-[#1E5EDB]/30">
              <CalendarDays size={30} className="text-[#1E5EDB]" />
            </span>
            <div>
              <h3 className="font-display text-2xl font-extrabold text-[#0A2342]">Ready to Maximize Your Solar Investment?</h3>
              <p className="mt-1 text-[15px] text-[#5B6B7F]">Clean panels produce more energy. Let Solar Pros Cleaning Services help you.</p>
            </div>
          </div>
          <button onClick={openQuote} data-testid="contact-final-cta" className={BTN_PRIMARY}>GET A FREE QUOTE</button>
        </div>
      </section>
    </div>
  );
}
